import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { flow, types, cast } from "mobx-state-tree";
import { UserProfile, type IUserProfile } from "./models";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";

// Helper to map database row to UserProfile model
const mapProfile = (row: Database["public"]["Tables"]["user_profiles"]["Row"]): IUserProfile => {
  return {
    id: row.id,
    email: row.email,
    org_id: undefined,
    first_name: row.first_name ?? undefined,
    last_name: row.last_name ?? undefined,
    phone_number: row.phone_number ?? undefined,
    job_title: row.job_title ?? undefined,
    company: row.company ?? undefined,
    avatar_url: row.avatar_url ?? undefined,
    color: row.color ?? undefined,
    allow_permanent_delete: row.allow_permanent_delete ?? undefined,
    tos_accepted_at: row.tos_accepted_at ?? undefined,
    pm_digest_opt_out: row.pm_digest_opt_out ?? undefined,
    message_board_opt_out: row.message_board_opt_out ?? undefined,
  };
};

/**
 * UserStore model for managing user profile data.
 * Handles fetching and updating user profile information.
 */
export const UserStore = types
  .model("UserStore", {
    userProfile: types.maybeNull(UserProfile),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    /** Get the full name of the user */
    get fullName(): string {
      if (!self.userProfile) return "";
      const firstName = self.userProfile.first_name || "";
      const lastName = self.userProfile.last_name || "";
      return `${firstName} ${lastName}`.trim();
    },

    /** Check if profile has been loaded */
    get isProfileLoaded(): boolean {
      return self.userProfile !== null;
    },

    /** Get initials for avatar display */
    get initials(): string {
      if (!self.userProfile) return "";
      const firstName = self.userProfile.first_name || "";
      const lastName = self.userProfile.last_name || "";
      return `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();
    },

    /** True once `user_profiles.tos_accepted_at` is set for the loaded profile. */
    get hasAcceptedTos(): boolean {
      return self.userProfile?.tos_accepted_at != null;
    },
  }))
  .actions((self) => ({
    setProfile(profile: SnapshotIn<typeof UserProfile> | null) {
      self.userProfile = cast(profile);
    },

    setLoading(loading: boolean) {
      self.isLoading = loading;
    },

    setError(error: string | null) {
      self.error = error;
    },

    loadProfile: flow(function* loadProfile(authUserId: string) {
      self.isLoading = true;
      self.error = null;

      try {
        const { data: profileRow, error: profileError } = yield supabase
          .from("user_profiles")
          .select(
            "id,email,first_name,last_name,phone_number,job_title,company,avatar_url,color,allow_permanent_delete,auth_user_id,tos_accepted_at,pm_digest_opt_out,message_board_opt_out"
          )
          .eq("auth_user_id", authUserId)
          .maybeSingle();

        if (profileError) {
          self.error = profileError.message;
          return null;
        }

        if (!profileRow) {
          self.userProfile = null;
          return null;
        }

        const profile = mapProfile(profileRow);
        self.userProfile = cast(profile);
        return profile;
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
        return null;
      } finally {
        self.isLoading = false;
      }
    }),

    updateProfile: flow(function* updateProfile(
      authUserId: string,
      updates: {
        first_name?: string;
        last_name?: string;
        phone_number?: string;
        job_title?: string;
        company?: string;
        avatar_url?: string | null;
        color?: string | null;
        pm_digest_opt_out?: boolean;
        message_board_opt_out?: boolean;
      }
    ) {
      self.error = null;

      try {
        if (!self.userProfile) {
          const errorMsg = "Cannot update profile: No profile loaded. Please reload the page.";
          self.error = errorMsg;
          throw new Error(errorMsg);
        }

        if (!authUserId) {
          const errorMsg = "Cannot update profile: User not authenticated.";
          self.error = errorMsg;
          throw new Error(errorMsg);
        }

        const { error: updateError } = yield supabase
          .from("user_profiles")
          .update(updates)
          .eq("auth_user_id", authUserId);

        if (updateError) {
          self.error = updateError.message;
          throw updateError;
        }

        // Only update local state after successful database update
        if (updates.first_name !== undefined) {
          self.userProfile.first_name = updates.first_name || undefined;
        }
        if (updates.last_name !== undefined) {
          self.userProfile.last_name = updates.last_name || undefined;
        }
        if (updates.phone_number !== undefined) {
          self.userProfile.phone_number = updates.phone_number || undefined;
        }
        if (updates.job_title !== undefined) {
          self.userProfile.job_title = updates.job_title || undefined;
        }
        if (updates.company !== undefined) {
          self.userProfile.company = updates.company || undefined;
        }
        if (updates.avatar_url !== undefined) {
          self.userProfile.avatar_url = updates.avatar_url ?? undefined;
        }
        if (updates.color !== undefined) {
          self.userProfile.color = updates.color ?? undefined;
        }
        if (updates.pm_digest_opt_out !== undefined) {
          self.userProfile.pm_digest_opt_out = updates.pm_digest_opt_out;
        }
        if (updates.message_board_opt_out !== undefined) {
          self.userProfile.message_board_opt_out = updates.message_board_opt_out;
        }

        return true;
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),

    acceptTos: flow(function* acceptTos(authUserId: string) {
      self.error = null;
      try {
        const now = new Date().toISOString();
        // `.select()` is what makes the write verifiable: an UPDATE matching NO row
        // (RLS denial, an auth_user_id with no profile) returns `error: null` and is
        // otherwise indistinguishable from success — the modal would close, the local
        // flag would flip, and the user would be asked again next session with no
        // record of consent ever written.
        const { data, error } = yield supabase
          .from("user_profiles")
          .update({ tos_accepted_at: now })
          .eq("auth_user_id", authUserId)
          .select("id");

        if (error) {
          self.error = error.message;
          return false;
        }

        if (!data?.length) {
          self.error = "Consent was not recorded: no user profile matched.";
          return false;
        }

        if (self.userProfile) {
          self.userProfile.tos_accepted_at = now;
        }
        return true;
      } catch (e: unknown) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),

    clearProfile() {
      self.userProfile = null;
      self.error = null;
      self.isLoading = false;
    },
  }));

export type UserStoreInstance = Instance<typeof UserStore>;
export type UserStoreSnapshotIn = SnapshotIn<typeof UserStore>;
export type UserStoreSnapshotOut = SnapshotOut<typeof UserStore>;
